import type { PreviewFileParams } from "./types";
import { FileErrorType, fileRenderType } from "./types";
import { useMount, useRequest } from "ahooks";
import { getRenderType, hideLeftMenu, showLeftMenu, wordExtensions } from "./utils";
import { useState, useEffect } from "react";
import AppSetting from "config/AppSetting";
import { cn, wrapRequestToPromise } from "utils/index";
import ProcedureDetailService from "service/procedureFile/ProcedureDetailService";
import { Button, message } from "antd";

import { downloadPDF } from "app/commonComponents/fileManagementFields/coverSheet";
import { Spin, ConfigProvider as ConfigProvider5 } from "antd-v5";
import { createPortal } from "react-dom";
import NormalSpin from "base/basicComponents/spin";

// Import react-pdf-viewer
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { toolbarPlugin, ToolbarSlot, TransformToolbarSlot } from "@react-pdf-viewer/toolbar";
import { RegisterFileSharePayload } from "service/procedureFile/types";
import { useLocation } from "react-router-dom";
import errorImg from "assets/image/onlineViewError.png";
import classNames from "classnames";
import { InfoCircleFilled } from "@ant-design/icons";

const CoverSheetPreviewType = "a6c9df7e-ac90-43a7-9944-207fc6a82e9c";

const registerFileShareParam = {
  ProductKey: AppSetting.urlPrefix.addFile.ProductKey!,
  storageFileID: [],
  expiry: {
    expiry: 1,
    expiryType: "Days",
  },
};

const transform: TransformToolbarSlot = (slot: ToolbarSlot) => {
  return Object.assign({}, slot, {
    Open: () => null,
    Download: () => null,
  });
};

const PreviewFile = () => {
  const [result, setResult] = useState({
    url: "",
    type: "",
    fileName: "",
  });
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const toolbarPluginInstance = toolbarPlugin();
  const { renderDefaultToolbar } = toolbarPluginInstance;
  const searchParams = new URLSearchParams(location.search);
  const fileError = searchParams.get("fileError");

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    renderToolbar: (Toolbar) => <Toolbar>{renderDefaultToolbar(transform)}</Toolbar>,
    toolbarPlugin: {
      zoomPlugin: {
        enableShortcuts: false, // Disables keyboard shortcuts for zoom
      },
    },
  });

  useEffect(() => {
    // Clean up the blob URL when component unmounts or URL changes
    return () => {
      if (result.url.startsWith("blob:")) {
        URL.revokeObjectURL(result.url);
      }
    };
  }, [result.url]);

  const downloadFile = async () => {
    setLoading(true);
    const response = await fetch(result.url).catch(() => {
      message.error("Failed to download this file.");
      setLoading(false);
    });
    if (!response) {
      message.error("Failed to download this file.");
      setLoading(false);
      return;
    }
    const blob = await response.blob();
    downloadPDF(blob, result.fileName);
    setLoading(false);
  };

  useMount(() => {
    const result = localStorage.getItem("previewLink");
    if (!result) {
      showLeftMenu();
      window.location.href = "/ProcedureList";
      return;
    }
    const parsedResult = JSON.parse(result);
    setResult(parsedResult);

    hideLeftMenu();
    localStorage.removeItem("previewLink");
  });

  const showError = fileError === FileErrorType.FileSizeZero;
  const isWordDoc = wordExtensions.some((ext) => result.fileName.toLowerCase().endsWith(ext));
  return (
    <div className="h-full w-full overflow-hidden">
      <NormalSpin spinning={loading} size="large">
        <div className="flex flex-col h-full">
          <div
            className={classNames("flex justify-between items-center bg-[#eee] py-[.375rem] px-5", {
              "bg-[#fffbe6]": isWordDoc,
            })}
          >
            {isWordDoc && (
              <>
                <div className="flex gap-x-2 items-center">
                  <InfoCircleFilled style={{ color: "#faad14" }} />
                  <span className="text-sm">
                    Please note that comments and tracked changes in Word documents are not
                    supported in the online preview. If needed, kindly download the document to view
                    these details.
                  </span>
                </div>
                <div className="absolute right-0 bottom-0 bg-white w-[5.5rem] h-[1.3125rem]"></div>
              </>
            )}
            <Button
              icon={<DownloadIcon />}
              className="border border-solid border-[#497EDC] rounded text-[#497EDC] flex items-center gap-x-1 font-medium text-sm leading-[1.125rem] ml-auto"
              onClick={downloadFile}
              data-testid="preview-file-download-btn"
            >
              Download
            </Button>
          </div>
          {showError && (
            <div className="flex flex-col h-full items-center pt-32">
              <img src={errorImg} alt="Error" className="w-8 h-8" />
              <span>
                This document is empty and cannot be previewed. Please download it to view.
              </span>
            </div>
          )}

          {/* doc/docx */}
          {result.url && result.type === fileRenderType[0] && !showError && isWordDoc && (
            <iframe
              title="customRender"
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(result.url)}&ui=en-US&action=view`}
              className="w-full border-none flex-1"
            />
          )}

          {/* xls/xlsx */}
          {result.url && result.type === fileRenderType[0] && !showError && !isWordDoc && (
            <div className="flex-1 overflow-hidden">
              <iframe
                title="customRender"
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(result.url)}&ui=en-US&action=view`}
                className="w-full border-none"
                style={{ height: "calc(100% + 26px)" }}
              />
            </div>
          )}

          {/* pdf - Using react-pdf-viewer */}
          {result.url &&
            !showError &&
            (result.type === fileRenderType[1] || result.type === CoverSheetPreviewType) && (
              <div className="w-full" style={{ height: "calc(100vh - 140px)" }}>
                <Worker workerUrl={`https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js`}>
                  <Viewer
                    fileUrl={result.url}
                    plugins={[defaultLayoutPluginInstance]}
                    characterMap={{
                      isCompressed: true,
                      // The url has to end with "/"
                      url: "https://unpkg.com/pdfjs-dist@3.4.120/cmaps/",
                    }}
                    defaultScale={1.5}
                    transformGetDocumentParams={(options) => ({
                      ...options,
                      isEvalSupported: false,
                    })}
                  />
                </Worker>
              </div>
            )}
        </div>
      </NormalSpin>
    </div>
  );
};

const PreviewFileLink = ({
  children,
  fileName,
  fileId,
  className,
  onClick = () => {},
  allowDownload = true,
  ...rest
}: PreviewFileParams & React.ComponentProps<"a">) => {
  const paramsError = !fileId || !fileName;

  const { run: runDownloadFileDirectly, loading } = useRequest(
    wrapRequestToPromise(ProcedureDetailService.downloadFile),
    {
      manual: true,
      onSuccess: async (result) => {
        const blob = await result.blob();
        downloadPDF(blob, fileName);
      },
      onError: () => {
        message.error("Failed to download this file.");
      },
    }
  );

  const { runAsync: runAsyncCheckFile, loading: checkFileLoading } = useRequest(
    wrapRequestToPromise(ProcedureDetailService.downloadFile),
    {
      manual: true,
    }
  );

  const { runAsync: runAsyncGetFileLink, loading: swapLinkLoading } = useRequest(
    wrapRequestToPromise(ProcedureDetailService.registerFileShare),
    {
      manual: true,
    }
  );

  const { run: runGetBlob, loading: getBlobLoading } = useRequest(
    wrapRequestToPromise(ProcedureDetailService.downloadFile),
    {
      manual: true,
      onSuccess: async (result) => {
        const blob = await result.blob();
        const blobUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
        localStorage.setItem(
          "previewLink",
          JSON.stringify({ url: blobUrl, fileName, type: renderType })
        );

        const url = new URL("/PreviewFile", window.location.origin);
        if (blob.size === 0) {
          url.searchParams.set("fileError", FileErrorType.FileSizeZero);
        }
        window.open(url.toString(), "_blank", "noopener,noreferrer");
      },
      onError: () => {
        message.error("Failed to download this file.");
      },
    }
  );

  const redirectFileForType0 = async (payload: RegisterFileSharePayload) => {
    try {
      const [checkRes, linkRes] = await Promise.all([
        runAsyncCheckFile(payload),
        runAsyncGetFileLink(payload),
      ]);
      if (!checkRes || !linkRes?.length) {
        throw new Error("Invalid response");
      }

      const blob = await checkRes.blob();
      const url = new URL("/PreviewFile", window.location.origin);
      localStorage.setItem(
        "previewLink",
        JSON.stringify({ url: linkRes[0].shareUrl, fileName, type: renderType })
      );
      if (blob.size === 0) {
        url.searchParams.set("fileError", FileErrorType.FileSizeZero);
      }
      window.open(url.toString(), "_blank", "noopener,noreferrer");
    } catch (error) {
      message.error("Failed to open this file.");
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (!allowDownload || paramsError) return;
    e.preventDefault();

    if (!renderType && fileId !== CoverSheetPreviewType) {
      // unsupported type, download directly
      runDownloadFileDirectly({ ...registerFileShareParam, storageFileID: [fileId] });
    } else if (renderType === fileRenderType[0]) {
      // doc/docx/xls/xlsx...
      redirectFileForType0({ ...registerFileShareParam, storageFileID: [fileId] });
    } else if (renderType === fileRenderType[1] && fileId !== CoverSheetPreviewType) {
      // other pdf not including cover sheet
      runGetBlob({ ...registerFileShareParam, storageFileID: [fileId] });
    }
    // call the original onClick to ensure cover sheet preview works
    onClick(e);
  };

  if (paramsError) {
    return children;
  }
  const renderType = getRenderType(fileName);

  return (
    <ConfigProvider5
      theme={{
        components: {
          Spin: {
            colorBgMask: "rgba(255, 255, 255, 0.5)",
            colorWhite: "#1890ff",
          },
        },
      }}
    >
      <a
        {...rest}
        target="_blank"
        rel="noopener noreferrer"
        className={cn("underline-offset-4 !underline !cursor-pointer", className)}
        onClick={handleClick}
        data-testid={`preview-file-link-${fileId}`}
      >
        {children}
      </a>
      {createPortal(
        <Spin
          spinning={loading || swapLinkLoading || getBlobLoading || checkFileLoading}
          size="large"
          fullscreen
        />,
        document.body
      )}
    </ConfigProvider5>
  );
};

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.90156 9.32813C6.91326 9.34307 6.92819 9.35515 6.94525 9.36346C6.96231 9.37177 6.98103 9.37609 7 9.37609C7.01897 9.37609 7.03769 9.37177 7.05475 9.36346C7.0718 9.35515 7.08674 9.34307 7.09844 9.32813L8.84844 7.11406C8.9125 7.03281 8.85469 6.9125 8.75 6.9125H7.59219V1.625C7.59219 1.55625 7.53594 1.5 7.46719 1.5H6.52969C6.46094 1.5 6.40469 1.55625 6.40469 1.625V6.91094H5.25C5.14531 6.91094 5.0875 7.03125 5.15156 7.1125L6.90156 9.32813ZM12.7188 8.78125H11.7812C11.7125 8.78125 11.6562 8.8375 11.6562 8.90625V11.3125H2.34375V8.90625C2.34375 8.8375 2.2875 8.78125 2.21875 8.78125H1.28125C1.2125 8.78125 1.15625 8.8375 1.15625 8.90625V12C1.15625 12.2766 1.37969 12.5 1.65625 12.5H12.3438C12.6203 12.5 12.8438 12.2766 12.8438 12V8.90625C12.8438 8.8375 12.7875 8.78125 12.7188 8.78125Z"
      fill="currentColor"
    />
  </svg>
);

export { PreviewFileLink, CoverSheetPreviewType };

export default PreviewFile;
